import { z } from 'zod';
import { IANATimezoneSchema } from '../common/timezone.schema.js';
import { FacilityCapabilitySchema } from './capability.schema.js';
import { HstTreatmentSchema } from '../rate/enums.schema.js';
import { DunningProfileSchema, FacilityStatusSchema, FacilityTypeSchema, InvoiceCadenceSchema, } from './inquiry.schema.js';
/** Subset of facility_contract returned to facility users — operational +
 *  financial terms they need to know about their own contract, without
 *  internal IDs or doc keys. */
export const FacilityContractSummarySchema = z.object({
    effectiveFrom: z.string().date(),
    effectiveTo: z.string().date().nullable(),
    poReference: z.string().nullable(),
    invoiceCadence: InvoiceCadenceSchema,
    netTermsDays: z.number().int(),
    dunningProfile: DunningProfileSchema,
    interestPerMonthPct: z.number(),
    /** True when a signed contract PDF is attached — the facility can download it
     *  via GET /me/facility/profile/contract-doc. The doc key itself is never
     *  exposed to facility users (operator-owned). */
    hasSignedDoc: z.boolean(),
});
/** Other portal users on the same facility — visible to all facility users
 *  so they know who else can post shifts / receive invoices for this
 *  facility. */
export const FacilityUserSummarySchema = z.object({
    userId: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string().nullable(),
    invitedAt: z.string().datetime(),
    acceptedAt: z.string().datetime().nullable(),
});
/** GET /api/v1/facilities/me — facility user's view of their facility.
 *
 *  Single facility per user at v1 (Q7 — facility users belong to one
 *  facility; the join table accommodates future cases without a schema
 *  change). 404 if the user has no facility_user_account row. */
export const FacilityMeResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    dbaName: z.string().nullable(),
    type: FacilityTypeSchema,
    status: FacilityStatusSchema,
    /** Per-facility HST treatment — READ-ONLY here (only the operator changes it; the
     *  platform is merchant of record + makes the tax determination). Shown so a facility
     *  can confirm its own tax status. (2026-09-21) */
    hstTreatment: HstTreatmentSchema,
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    province: z.string(),
    postalCode: z.string().nullable(),
    /** IANA timezone — drives Phase 8+ shift-time-bucket math + Phase
     *  10+ booking local-time display. */
    timezone: IANATimezoneSchema,
    primaryContactName: z.string().nullable(),
    primaryContactRole: z.string().nullable(),
    primaryContactEmail: z.string().nullable(),
    primaryContactPhone: z.string().nullable(),
    billingContactEmail: z.string().nullable(),
    /** The currently-active contract row (effective_to IS NULL), or null
     *  if no contract has been signed yet (PROSPECT facilities, edge cases
     *  where invites were sent before contracting). */
    currentContract: FacilityContractSummarySchema.nullable(),
    users: z.array(FacilityUserSummarySchema),
    /** RBAC 3.2 — the CALLER's own capability set at this facility. The portal
     *  reads this to hide surfaces the login can't use (no "Post a shift" tab
     *  without SHIFT_POST, no Invoices tab without INVOICE_VIEW). The backend
     *  guard is the real enforcement; this is UX so a user isn't shown a button
     *  that 403s. */
    myCapabilities: z.array(FacilityCapabilitySchema),
});
//# sourceMappingURL=self.schema.js.map